import React, { Dispatch, SetStateAction, useState, useEffect } from 'react';
import { Box, Chip, Typography, FormControlLabel, Checkbox } from '@mui/material';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import { Fields } from '../../../ui-component/FormikFormLibrary/FormikTypes';
import { httpRequest } from 'src/services/httpRequest';
import { handleError } from 'src/redux-store/slices/errorSlice';
import { useDispatch, useSelector } from 'react-redux';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { FormikRadioButton } from 'src/ui-component/FormikFormLibrary/FormikRadioButton';
import { RootState } from 'src/redux-store/appStore';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import { setUserDetails } from 'src/redux-store/slices/userSlice';
import { format } from 'date-fns';
import { Method } from 'axios';

type AddNewUserProps = {
  setUserAction: Dispatch<SetStateAction<string>>;
  userAction: any;
  handleApiCall: () => void;
};

interface ProfileDataTypes {
  fristname: string;
  lastname: string;
  phonenumber: string;
  userrole: string;
}

interface SubCode {
  SubcodeNameID: string;
  SubcodeName: string;
}

const AddNewUser: React.FC<AddNewUserProps> = ({
  setUserAction,
  userAction,
  handleApiCall
}) => {
  const dispatch = useDispatch();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const [loading, setLoading] = useState<boolean>(false);
  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [IsOpen, setIsOpen] = useState<boolean>(false);
  const [isInspector, setIsInspector] = useState<boolean>(userAction?.UserRole === 'Inspector');
  const [subCodes, setSubCodes] = useState<SubCode[]>([]);
  const [profileFormData, setProfileFormData] = useState<ProfileDataTypes>({
    fristname: '',
    lastname: '',
    phonenumber: '',
    userrole: ''
  });
  const isAbleToInactiveUser = !(
    Number(userDetails?.UserID) === Number(userAction?.UserID) &&
    userAction?.UserRole === 'Admin'
  );
  const [userActiveInactive, setUserActiveInactive] = useState<boolean>(
    userAction?.Active
  );
  const { townshipId } = useParams<{ townshipId: string }>();

  const fetchSubCodes = async () => {
    setLoading(true);
    try {
      const response = await httpRequest(
        'GET',
        '/construction/application/all/subCodes'
      );
      if (response.status === 'success') {
        setSubCodes(response.data || []);
      } else {
        toast.error('Failed to fetch SubCodes');
        dispatch(handleError(response?.message));
      }
    } catch (error) {
      toast.error('Error fetching SubCodes');
      dispatch(handleError('Error fetching SubCodes'));
    } finally {
      setLoading(false);
    }
  };

// 1. Add this helper function to prepare existing files for display
const getExistingFiles = () => {
  if (userAction?.InspectorProfile?.InspectorCertificatePath) {
    return [{
      name: 'Inspector License Document',
      url: userAction.InspectorProfile.InspectorCertificatePath,
      type: 'existing' as const,
      id: 'existing_license_doc'
    }];
  }
  return [];
};

  // Fetch subCodes when adding an inspector
  useEffect(() => {
    if (isInspector) {
      fetchSubCodes();
    }
  }, [isInspector]);

  // Construct fields based on whether inspector checkbox is checked
  const getFields = (): Fields[] => {
    const baseFields: Fields[] = [
      {
        fieldName: 'fristname',
        fieldLabel: 'First Name',
        fieldType: 'text',
        required: true,
        initialValue: userAction?.FirstName || ''
      },
      {
        fieldName: 'lastname',
        fieldLabel: 'Last Name',
        fieldType: 'text',
        required: true,
        initialValue: userAction?.LastName || ''
      },
      {
        fieldName: 'email',
        fieldLabel: 'User Email',
        fieldType: 'email',
        disabled: userAction !== 'Add',
        required: true,
        initialValue: userAction.EmailID || ''
      },
      {
        fieldName: 'phonenumber',
        fieldLabel: 'Phone Number',
        fieldType: 'mobile',
        required: true,
        initialValue: userAction.PhoneNumber || ''
      },
      {
        fieldName: 'userrole',
        fieldLabel: 'User Role',
        fieldType: 'select',
        options: isInspector
          ? ['Admin', 'User', 'Inspector']
          : ['Admin', 'User'],
        disabled: isInspector,
        required: true,
        br: true,
        initialValue: isInspector ? 'Inspector' : userAction.UserRole || ''
      }
    ];

    // Add inspector-specific fields if inspector checkbox is checked
    if (isInspector) {
      return [
        ...baseFields,
        {
          fieldName: 'licenseNumber',
          fieldLabel: 'License Number',
          fieldType: 'text',
          required: true,
          placeHolder: 'LIC123456',
          initialValue: userAction?.InspectorProfile?.LicenseNumber || ''
        },
        {
          fieldName: 'licenseIssueDate',
          fieldLabel: 'License Issue Date',
          fieldType: 'date',
          required: true,
          initialValue: userAction?.InspectorProfile?.LicenseIssueDate || null
        },
        {
          fieldName: 'licenseExpiryDate',
          fieldLabel: 'License Expiry Date',
          fieldType: 'date',
          required: true,
          initialValue: userAction?.InspectorProfile?.LicenseExpiryDate || null
        },
        {
          fieldName: 'inspectionsPerDay',
          fieldLabel: 'Inspections Per Day',
          fieldType: 'number',
          required: true,
          initialValue: userAction?.InspectorProfile?.InspectionsPerDay || 0
        },
        {
          fieldName: 'subCodes',
          initialValue: userAction?.InspectorProfile?.SubCodes || [],
          fieldLabel: 'SubCodes',
          fieldType: 'multiselect',
          options: subCodes.map(code => code.SubcodeName),
          required: true,
          multiSelect: true,
          br: true
        },
        {
          fieldName: 'DocumentFileUpload',
          fieldLabel: 'Upload Document',
          fieldType: 'file',
          isErrorField: false,
          required: userAction === 'Add', 
          listUI: true,
          placement: 'start',
          multiSelect: false,
          disabled: false,
          existingFiles: getExistingFiles(), 
          aboveParagraph: (
            <Typography>
              Please upload scanned copy or photo of the issued license.
              {/* <br /> Only image files (JPG, PNG, GIF, SVG, HEIC) and PDF files are
              allowed, with maximum file size of 10MB. */}
            </Typography>
          )
        }
      ];
    }

    return baseFields;
  };

  async function handleSubmit(value: any) {
    const body: {
      FirstName: string;
      LastName: string;
      PhoneNumber: string | number;
      Role?: string;
      EmailID?: string;
      Active?: boolean;
      LicenseNumber?: string;
      LicenseIssueDate?: string;
      LicenseExpiryDate?: string;
      InspectionsPerDay?: number;
      SubCodes?: string[];
      InspectorCertificatePath?: string;
      InspectorID?: number; // Add this for inspector updates
      Status?: string; // Add this for inspector updates
    } = {
      FirstName: value?.fristname || profileFormData?.fristname,
      LastName: value?.lastname || profileFormData?.lastname,
      PhoneNumber: value?.phonenumber || profileFormData?.phonenumber,
    };
  
    setLoading(true);
  
    if (userAction === 'Add') {
      body.EmailID = value.email;
  
      // Add inspector specific fields if applicable
      if (isInspector) {
        // Handle file upload for new inspector
        if (value.DocumentFileUpload && value.DocumentFileUpload.length > 0) {
          try {
            const formData = new FormData();
            formData.append('File', value.DocumentFileUpload[0]);
            const response = await httpRequest('POST', `/uploadPDF/document_Images?PermitName=Construction&FileType=Inspector_License`, formData,
              {
                'Content-Type': 'multipart/form-data',
              })
            body.InspectorCertificatePath = response?.data?.FilePath;
          } catch (uploadError) {
            setLoading(false);
            dispatch(handleError('File upload failed. Please try again.'));
            return;
          }
        }
  
        body.LicenseNumber = value.licenseNumber;
        body.LicenseIssueDate = format(new Date(value.licenseIssueDate), 'yyyy-MM-dd');
        body.LicenseExpiryDate = format(new Date(value.licenseExpiryDate), 'yyyy-MM-dd');
        body.InspectionsPerDay = parseInt(value.inspectionsPerDay, 10);
        body.SubCodes = value.subCodes.map(selectedName => {
          const foundSubcode = subCodes.find(code => code.SubcodeName === selectedName);
          return foundSubcode?.SubcodeNameID;
        }).filter(id => id !== undefined);
      } else {
        body.Role = value?.userrole || profileFormData?.userrole;
      }
    } else {
      // Handle UPDATE cases
      if (userAction?.UserRole === 'Inspector') {
        // INSPECTOR UPDATE CASE
        body.InspectorID = userAction.UserID;
        body.EmailID = userAction.EmailID; // Keep existing email for updates
        body.Status = userActiveInactive ? 'Active' : 'Inactive';
  
        // Handle file upload for inspector update
        if (value.DocumentFileUpload && value.DocumentFileUpload.length > 0) {
          try {
            const formData = new FormData();
            formData.append('File', value.DocumentFileUpload[0]);
            const response = await httpRequest('POST', `/uploadPDF/document_Images?PermitName=Construction&FileType=Inspector_License`, formData,
              {
                'Content-Type': 'multipart/form-data',
              })
            body.InspectorCertificatePath = response?.data?.FilePath;
          } catch (uploadError) {
            setLoading(false);
            dispatch(handleError('File upload failed. Please try again.'));
            return;
          }
        } else if (userAction?.InspectorProfile?.InspectorCertificatePath) {
          // Keep existing file if no new file uploaded
          body.InspectorCertificatePath = userAction.InspectorProfile.InspectorCertificatePath;
        }
  
        // Add all inspector-specific fields for update
        body.LicenseNumber = value.licenseNumber || userAction?.InspectorProfile?.LicenseNumber;
        body.LicenseIssueDate = value.licenseIssueDate 
          ? format(new Date(value.licenseIssueDate), 'yyyy-MM-dd')
          : userAction?.InspectorProfile?.LicenseIssueDate;
        body.LicenseExpiryDate = value.licenseExpiryDate 
          ? format(new Date(value.licenseExpiryDate), 'yyyy-MM-dd')
          : userAction?.InspectorProfile?.LicenseExpiryDate;
        body.InspectionsPerDay = value.inspectionsPerDay 
          ? parseInt(value.inspectionsPerDay, 10)
          : userAction?.InspectorProfile?.InspectionsPerDay;
        
        if (value.subCodes && value.subCodes.length > 0) {
          body.SubCodes = value.subCodes.map(selectedName => {
            const foundSubcode = subCodes.find(code => code.SubcodeName === selectedName);
            return foundSubcode?.SubcodeNameID;
          }).filter(id => id !== undefined);
        } else if (userAction?.InspectorProfile?.SubCodes) {
          body.SubCodes = userAction.InspectorProfile.SubCodes;
        }
  
      } else {
        // REGULAR USER UPDATE CASE
        body.Active = userActiveInactive;
      }
    }
  
    // Determine the correct endpoint and method
    let endpoint: string;
    let method: Method;
  
    if (userAction === 'Add') {
      if (isInspector) {
        endpoint = '/auth/inspector';
        method = 'POST';
      } else {
        endpoint = `/auth/register/${townshipId}`;
        method = 'POST';
      }
    } else {
      // Update cases
      if (userAction?.UserRole === 'Inspector') {
        endpoint = '/auth/inspector_update';
        method = 'PATCH'; // Use PATCH for inspector updates
      } else {
        endpoint = `/auth/updateUser/${userAction.UserID}`;
        method = 'PUT';
      }
    }
      // eslint-disable-next-line no-console
      console.log("body", body, value, endpoint);
      // return
    try {
      const response = await httpRequest(method, endpoint, body);
  
      if (response.status === 'success') {
        if (Number(userDetails?.UserID) === Number(userAction?.UserID)) {
          dispatch(setUserDetails({
            ...userDetails,
            FirstName: value?.fristname || profileFormData?.fristname,
            LastName: value?.lastname || profileFormData?.lastname
          }));
        }
  
        setIsOpen(false);
        toast.success(
          `${userAction?.UserRole === 'Inspector' ? 'Inspector' : 'User'} ${userAction === 'Add' ? 'added' : 'updated'} successfully.`
        );
        setUserAction('');
        handleApiCall();
        setLoading(false);
      } else {
        setIsOpen(false);
        toast.error(response?.message);
        setUserAction('');
        setLoading(false);
        dispatch(handleError(response?.message));
      }
    } catch (error) {
      setIsOpen(false);
      toast.error('An error occurred while processing your request.');
      setUserAction('');
      setLoading(false);
      dispatch(handleError('Network error occurred'));
    }
  }

  //Method to cancel the form
  function handleCancel() {
    setUserAction('');
  }

  //Method to check if the user is active or inactive
  const Status = () => (
    <Chip
      label={userAction?.Active ? 'Active' : 'Inactive'}
      color={userAction?.Active ? 'success' : 'error'}
      variant="outlined"
      sx={{ fontWeight: 'bold', ml: 1 }}
    />
  );

  //Method to change the status of the user
  const radioContent = () => {
    const handleRadioChange = (e) => {
      setIsChanged(true);
      setUserActiveInactive(e.target.value === 'Active');
    };
    return Number(userDetails?.UserID) === Number(userAction?.UserID) &&
      userAction?.UserRole === 'Admin' ? null : (
      <Box mt={1}>
        <FormikRadioButton
          disabled={false}
          label={'Set this user to active/Inactive'}
          fieldValue={{ value: userActiveInactive ? 'Active' : 'Inactive' }}
          options={[
            { value: 'Active', label: 'Active' },
            { value: 'Inactive', label: 'Inactive' }
          ]}
          handleChange={(e) => handleRadioChange(e)}
        />
      </Box>
    );
  };

  // Custom form header to add inspector checkbox
  const formHeader = () => {
    // Only show inspector checkbox when adding a new user
    if (userAction !== 'Add') return null;

    const handleInspectorChange = (e) => {
      setIsInspector(e.target.checked);
    };

    return (
      <Box mb={2}>
        <FormControlLabel
          control={
            <Checkbox
              checked={isInspector}
              onChange={handleInspectorChange}
              name="isInspector"
              color="primary"
            />
          }
          label="Add as Inspector"
        />
      </Box>
    );
  };

  return (
    <Box m={{ xs: 1.5, sm: 3 }}>
      {loading && <LoadingOverlay />}
      {userAction !== 'Add' ? (
        <Typography
          pb={2}
          fontSize={16}
          mb={2}
          color="#6E759F"
          className="weight-600"
        >
          Status: <Status />
        </Typography>
      ) : (
        formHeader()
      )}
      <FormikForm
        allFields={getFields()}
        buttonTitle={['Cancel', 'Save']}
        fullScreen={false}
        isEditModel={false}
        content={userAction !== 'Add' ? radioContent : false}
        buttonPlacement={'right'}
        defaultEnabledButton={isChanged ? ['Save'] : false}
        formMethod={
          isAbleToInactiveUser && userActiveInactive !== userAction?.Active
            ? (val) => {
              setProfileFormData(val)
              setIsOpen(true)
            }
            : handleSubmit
        }
        handleOtherButton={handleCancel}
      />
      <CustomModal
        title={`${userActiveInactive ? 'Active' : 'Inactive'} User?`}
        IsOpen={IsOpen}
        setIsOpen={setIsOpen}
        onSave={handleSubmit}
        actionButtons={['No', 'Yes']}
        loading={loading}
        content={
          <Typography>{`Are you sure you want to ${userActiveInactive ? 'active' : 'inactive'
            } this user?`}</Typography>
        }
      />
    </Box>
  );
};

export default AddNewUser;